/**
 * Asset lifecycle rules.
 *
 * These four are where this system is most likely to develop a real bug, and each
 * is enforced here, called by the API inside the transaction that does the write,
 * and covered by lifecycle.test.ts. The SPA calls the same functions to disable a
 * button - a courtesy, never the control.
 *
 * 1. The current holder of an asset is the ONE open Assignment row (returnedAt
 *    null). There is no `currentEmployeeId` column on Asset, because a denormalised
 *    holder and an assignment table are free to disagree, and then nothing can say
 *    which one lied. The database backs this with a partial unique index so even a
 *    double-clicked assign cannot open a second one.
 *
 * 2. Status transitions are a whitelist, not free assignment. The prototype let any
 *    status be typed over any other, so an asset could go from DISPOSED back to
 *    AVAILABLE and be handed to someone.
 *
 * 3. An employee still holding an asset cannot be marked CLEARED. The prototype
 *    showed a warning and then wrote the clearance anyway, which is exactly the
 *    record an audit later relies on being true.
 *
 * 4. An asset cannot be retired or disposed while someone still has it. Otherwise
 *    the register says the laptop is gone and an employee still has it in a bag.
 */

import type { AssetStatus, ClearanceStatus, EmployeeStatus } from './enums';
import { isArchivedAsset } from './enums';

/**
 * Legal next states for each status.
 *
 * Read this as "what can happen to a thing in this state". The absences carry the
 * rules: nothing leaves DISPOSED, ASSIGNED cannot jump straight to AVAILABLE
 * (a return is an event that has to be recorded, not a status edit), and RETIRED
 * can still be DISPOSED but never come back into service.
 */
const ALLOWED_TRANSITIONS: Record<AssetStatus, readonly AssetStatus[]> = {
  // A new asset, or one just returned or repaired.
  AVAILABLE: ['ASSIGNED', 'UNDER_REPAIR', 'LOST', 'DAMAGED', 'RETIRED', 'DISPOSED'],
  // Held by someone. AVAILABLE is reachable only by recording a return, which is
  // why it is not listed here - see assertReturnAllowed.
  ASSIGNED: ['UNDER_REPAIR', 'LOST', 'DAMAGED'],
  // ASSIGNED is reachable from here, and only from here, for one real case: the
  // laptop went to the vendor while the employee still had it. The assignment never
  // closed - they did not give it back, it simply left the building - so when the
  // repair closes the asset returns to the holder the register never stopped naming.
  // Every OTHER route to ASSIGNED goes through assertAssignAllowed, which demands
  // AVAILABLE; `assertNotManuallyAssigned` keeps the edit form off this path.
  UNDER_REPAIR: ['AVAILABLE', 'ASSIGNED', 'DAMAGED', 'LOST', 'RETIRED', 'DISPOSED'],
  // Found again, or written off. Not directly assignable: someone has to put it
  // back in service first, which is the AVAILABLE step.
  LOST: ['AVAILABLE', 'RETIRED', 'DISPOSED'],
  DAMAGED: ['UNDER_REPAIR', 'AVAILABLE', 'RETIRED', 'DISPOSED'],
  RETIRED: ['DISPOSED'],
  // Terminal. Scrapped, sold or handed to the e-waste vendor - there is no coming back.
  DISPOSED: [],
};

export class LifecycleError extends Error {
  /** Machine-readable, so the API can answer with a code the SPA switches on. */
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'LifecycleError';
    this.code = code;
  }
}

export function canTransition(from: AssetStatus, to: AssetStatus): boolean {
  // A no-op edit (saving a form without touching status) is not a transition.
  if (from === to) return true;
  return ALLOWED_TRANSITIONS[from].includes(to);
}

export function assertAssetTransition(from: AssetStatus, to: AssetStatus): void {
  if (canTransition(from, to)) return;
  throw new LifecycleError(
    'ILLEGAL_TRANSITION',
    from === 'DISPOSED'
      ? 'A disposed asset cannot change status. Register a replacement instead.'
      : `An asset that is ${label(from)} cannot become ${label(to)}.`,
  );
}

/**
 * Sending something to the vendor.
 *
 * Two rules on the API, one question here: `open()` refuses a second repair while one
 * is open (REPAIR_ALREADY_OPEN) and then asserts the transition. A button has to ask
 * both at once.
 *
 * The UNDER_REPAIR test is NOT redundant with canTransition. That one answers true for
 * from === to, deliberately - a no-op edit is not a transition - so on its own it would
 * offer a repair on an asset already sitting at the vendor, which is exactly the request
 * the API rejects. Status is a safe proxy for the repair row because open() writes
 * UNDER_REPAIR in the same transaction that creates it.
 */
export function canOpenRepair(status: AssetStatus): boolean {
  return status !== 'UNDER_REPAIR' && canTransition(status, 'UNDER_REPAIR');
}

/** Assigning: the asset must be free, and free means AVAILABLE and nothing else. */
export function canAssignAsset(status: AssetStatus, hasOpenAssignment: boolean): boolean {
  return !hasOpenAssignment && status === 'AVAILABLE';
}

export function assertAssignAllowed(status: AssetStatus, hasOpenAssignment: boolean): void {
  if (canAssignAsset(status, hasOpenAssignment)) return;
  if (hasOpenAssignment) {
    throw new LifecycleError(
      'ALREADY_ASSIGNED',
      'This asset is already issued to someone. Record its return first.',
    );
  }
  if (status !== 'AVAILABLE') {
    throw new LifecycleError(
      'NOT_ASSIGNABLE',
      `This asset is ${label(status)} and cannot be issued. Only an available asset can be.`,
    );
  }
}

/**
 * ASSIGNED is not a status anyone may type.
 *
 * It means "a named person has this", which is a fact about an assignment row, so
 * setting it from an edit form would claim a holder the register cannot name. The one
 * legitimate machine-written route is a repair closing on an asset that was never
 * given back - see the UNDER_REPAIR row above.
 *
 * Called by the asset edit path only; the assign and repair paths write the status
 * themselves and deliberately bypass this.
 */
export function assertNotManuallyAssigned(to: AssetStatus, from: AssetStatus): void {
  if (to === 'ASSIGNED' && from !== 'ASSIGNED') {
    throw new LifecycleError(
      'USE_ASSIGN_SCREEN',
      'Issue this asset from the Assign screen, so it is recorded who has it and from when.',
    );
  }
}

/**
 * Returning: there has to be something open to return, and the asset has to be in a
 * state where handing it back is physically possible.
 *
 * The status check is not decoration. An asset that is ASSIGNED and then sent for
 * repair keeps its open assignment - the employee never gave it back, it went to the
 * vendor - and the repair stamps `statusBeforeRepair: ASSIGNED` so it returns to that
 * holder when it closes. If a return were recorded in the meantime, the assignment
 * would close, and closing the repair would then restore the asset to ASSIGNED with
 * NO open assignment: the register would insist somebody holds it while being unable
 * to say who. That is precisely the invariant this whole model rests on, broken.
 *
 * It is also just true. You cannot hand back a laptop that is at the vendor.
 */
export function canReturnAsset(hasOpenAssignment: boolean, status?: AssetStatus): boolean {
  return hasOpenAssignment && status !== 'UNDER_REPAIR';
}

export function assertReturnAllowed(hasOpenAssignment: boolean, status?: AssetStatus): void {
  if (canReturnAsset(hasOpenAssignment, status)) return;
  if (!hasOpenAssignment) {
    throw new LifecycleError(
      'NOT_ASSIGNED',
      'This asset is not currently issued to anyone, so there is nothing to return.',
    );
  }
  if (status === 'UNDER_REPAIR') {
    throw new LifecycleError(
      'OUT_FOR_REPAIR',
      'This asset is out for repair. Close the repair first - it will go back to whoever was holding it.',
    );
  }
}

/**
 * The state an asset lands in once it comes back.
 *
 * Condition decides it, not the person recording the return: something handed back
 * visibly broken must not silently rejoin the pool and be issued to the next joiner.
 */
export function statusAfterReturn(condition: 'NEW' | 'GOOD' | 'FAIR' | 'POOR' | 'DAMAGED'): AssetStatus {
  return condition === 'DAMAGED' ? 'DAMAGED' : 'AVAILABLE';
}

/**
 * Retiring or disposing while someone still holds it would make the register say the
 * asset is gone while an employee still has it. Take it back first.
 */
export function assertArchiveAllowed(to: AssetStatus, hasOpenAssignment: boolean): void {
  if (isArchivedAsset(to) && hasOpenAssignment) {
    throw new LifecycleError(
      'STILL_ASSIGNED',
      `This asset is still issued to someone. Record its return before marking it ${label(to)}.`,
    );
  }
}

/**
 * AVAILABLE means nobody has it. Nothing else enforced that.
 *
 * The transition table cannot: UNDER_REPAIR → AVAILABLE is right for an asset that was
 * free when it went to the vendor and wrong for one the employee never gave back, and
 * only the open assignment tells those apart. `assertArchiveAllowed` deliberately says
 * nothing about non-archive states, so between them AVAILABLE had no owner - a repair
 * closed with a chosen result of AVAILABLE put a laptop back in the pool while it was
 * still on somebody's desk, and the register offered it to the next joiner.
 *
 * DAMAGED and LOST are deliberately NOT covered. Both are true of something an employee
 * is still holding, and refusing them would leave no way to record a laptop that broke
 * or vanished in their hands.
 */
export function assertNotStillHeld(to: AssetStatus, hasOpenAssignment: boolean): void {
  if (to === 'AVAILABLE' && hasOpenAssignment) {
    throw new LifecycleError(
      'STILL_ASSIGNED',
      'Someone still holds this asset, so it cannot go back in the pool. Record its return first, or send it back to them as Assigned.',
    );
  }
}

/**
 * Somebody can only leave once.
 *
 * Without this the service happily wrote a SECOND exit record and set EXITED again -
 * two exit dates for one person, and `outstandingAtClearance` snapshotted twice, so
 * the record that is supposed to be evidence of one day's state quietly became two.
 * A rehire is not the exception: reinstating clears the status first, which is exactly
 * what makes the next exit legitimate.
 *
 * NOTICE_PERIOD is deliberately allowed - that is the normal state to be exiting FROM.
 */
export function canRecordExit(status: EmployeeStatus): boolean {
  return status !== 'EXITED';
}

export function assertExitAllowed(status: EmployeeStatus): void {
  if (canRecordExit(status)) return;
  throw new LifecycleError(
    'ALREADY_EXITED',
    'This person is already recorded as having left. Reinstate them first if they came back.',
  );
}

/**
 * Rule 3: CLEARED means nothing outstanding, so it may not be written while the
 * person still holds company property.
 *
 * The three lesser outcomes stay open deliberately. An exit that ends with a lost
 * charger recovered from final settlement is a real outcome, and
 * CLEARED_WITH_COMMENTS records it honestly; forcing that case to pick between a
 * false CLEARED and a permanent PENDING is what makes exit records untrustworthy.
 */
export function assertClearanceAllowed(status: ClearanceStatus, openAssetCount: number): void {
  if (status === 'CLEARED' && openAssetCount > 0) {
    throw new LifecycleError(
      'ASSETS_OUTSTANDING',
      `${openAssetCount} asset${openAssetCount === 1 ? '' : 's'} still issued to this employee. ` +
        'Record the return, or clear them with comments explaining what is outstanding.',
    );
  }
}

/** Human-readable status, for a message someone has to act on. */
function label(status: AssetStatus): string {
  return status.toLowerCase().replace(/_/g, ' ');
}

/**
 * Warranty standing on a given day.
 *
 * `null` end date is "not recorded", which is NOT the same as expired - most
 * furniture has no warranty and flagging it red every day would train people to
 * ignore the colour.
 */
export type WarrantyState = 'NONE' | 'ACTIVE' | 'EXPIRING_SOON' | 'EXPIRED';

/** Anything inside this many days reads as expiring soon. One quarter's notice is
 *  enough to raise a renewal or plan a replacement. */
export const WARRANTY_EXPIRY_WINDOW_DAYS = 90;

export function warrantyState(
  warrantyEnd: string | null,
  today: string,
  windowDays: number = WARRANTY_EXPIRY_WINDOW_DAYS,
): WarrantyState {
  if (!warrantyEnd) return 'NONE';
  if (warrantyEnd < today) return 'EXPIRED';
  const days = Math.round(
    (Date.parse(`${warrantyEnd}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  return days <= windowDays ? 'EXPIRING_SOON' : 'ACTIVE';
}

/**
 * How long a physical verification stays good for.
 *
 * Half a year, not a quarter: the point of the number is that the count on the
 * dashboard should be a list somebody can actually work through. At 90 days a register
 * of several hundred assets is permanently overdue by hundreds, which reads as broken
 * rather than as a task, and a tile nobody can ever clear gets ignored - the same
 * failure as colouring every warranty-less chair red.
 *
 * Both apps read it: the API counts and filters against the cutoff, the SPA says the
 * number out loud in the tile's caption. Hardcoding it twice is how the caption ends up
 * claiming 90 days while the query uses 180.
 */
export const STOCK_TAKE_WINDOW_DAYS = 180;
